"use strict";

Object.defineProperty(exports, "__esModule", {
  value: true
});
exports.default = void 0;
var _react = _interopRequireWildcard(require("react"));
var _utils = require("@amzn/stencil-design-tokens/js/web/utils");
var _stencilReactDesc = require("@amzn/stencil-react-desc");
var _layout = require("../layout");
var _responsive = require("../responsive");
var _screenReaderOnly = require("../screen-reader-only");
var _spinner = require("../spinner");
var _text = require("../text");
var _utils2 = require("../utils");
var _constants = require("./constants");
var _inputFooter = require("./input-footer");
var _react2 = require("@emotion/react");
function _getRequireWildcardCache(e) { if ("function" != typeof WeakMap) return null; var r = new WeakMap(), t = new WeakMap(); return (_getRequireWildcardCache = function (e) { return e ? t : r; })(e); }
function _interopRequireWildcard(e, r) { if (!r && e && e.__esModule) return e; if (null === e || "object" != typeof e && "function" != typeof e) return { default: e }; var t = _getRequireWildcardCache(r); if (t && t.has(e)) return t.get(e); var n = { __proto__: null }, a = Object.defineProperty && Object.getOwnPropertyDescriptor; for (var u in e) if ("default" !== u && {}.hasOwnProperty.call(e, u)) { var i = a ? Object.getOwnPropertyDescriptor(e, u) : null; i && (i.get || i.set) ? Object.defineProperty(n, u, i) : n[u] = e[u]; } return n.default = e, t && t.set(e, n), n; }
const RecommendedLengthFooterBase = _ref => {
  let {
    children,
    progress,
    inputId,
    role: ariaRole = 'status',
    'aria-live': ariaLive = 'polite',
    'aria-atomic': ariaAtomic = 'true',
    'aria-relevant': ariaRelevant,
    shouldLiveAnnounce,
    borderRadius,
    __additionalInfo,
    ...transferProps
  } = _ref;
  const ref = (0, _react.useRef)(null);
  const isManualAnnouncement = shouldLiveAnnounce !== undefined;
  (0, _react.useEffect)(() => {
    if (ref.current && !ref.current.closest('[data-test-component = "StencilReactInputWrapper"]') && !(0, _utils2.envIsProduction)()) {
      console.warn('RecommendedLengthFooterBase should be used with InputWrapper');
    }
  }, []);
  const [isTyping, setIsTyping] = (0, _react.useState)(false);
  // aria-describedby is working like live region on Windows Chrome.
  // In order to avoid it need to unlink when textarea input is typed
  const [isLinkedToInput, setIsLinkedToInput] = (0, _react.useState)(true);
  const {
    error,
    warning,
    id,
    iconAltText
  } = transferProps;
  const icon = error || warning ? undefined : (0, _react2.jsx)(_spinner.ProgressArc, {
    "aria-hidden": true,
    color: (0, _utils.token)('color.icon.primary-default'),
    opacity: 0.15,
    progress: progress,
    size: 20,
    strokeWidth: 4
  });
  const keyUpTimeout = _react.default.useRef();
  const handleKeyDown = (0, _react.useCallback)(() => {
    setIsLinkedToInput(false);
    if (isManualAnnouncement) {
      return;
    }
    setIsTyping(true);
    if (keyUpTimeout.current) {
      clearTimeout(keyUpTimeout.current);
    }
  }, [isManualAnnouncement]);
  const handleKeyUp = (0, _react.useCallback)(e => {
    if (isManualAnnouncement || e.key === _utils2.EventKeys.TAB || e.key === _utils2.EventKeys.SHIFT) {
      return;
    }
    keyUpTimeout.current = window.setTimeout(() => {
      setIsTyping(false);
    }, 1000);
  }, [isManualAnnouncement]);
  const handleOnInputBlur = (0, _react.useCallback)(() => {
    setIsLinkedToInput(true);
  }, []);
  (0, _react.useEffect)(() => {
    if (!inputId) return;
    const inputEl = document.getElementById(inputId);
    if (!inputEl) return;
    inputEl.addEventListener('blur', handleOnInputBlur);
    inputEl.addEventListener('keydown', handleKeyDown);
    inputEl.addEventListener('keyup', handleKeyUp);
    return () => {
      inputEl.removeEventListener('keydown', handleKeyDown);
      inputEl.removeEventListener('keyup', handleKeyUp);
      inputEl.removeEventListener('blur', handleOnInputBlur);
      clearTimeout(keyUpTimeout.current);
    };
  }, [inputId, handleKeyDown, handleKeyUp, handleOnInputBlur]);
  let announcementText;
  if (isManualAnnouncement) {
    announcementText = shouldLiveAnnounce && children;
  } else {
    announcementText = !isTyping && children;
  }
  const viewport = (0, _responsive.useBreakpoints)();
  const isSmallViewport = viewport.matches.s;
  const {
    isInHighZoomLevel
  } = (0, _responsive.useHighZoomLevel)({
    level: 175
  });
  const isNotOnMobile = !isSmallViewport || isInHighZoomLevel;
  const appendedAnnouncementText = !!error ? (iconAltText || 'Error') + ' ' + announcementText : (iconAltText || 'Warning') + ' ' + announcementText;
  return (0, _react2.jsx)(_react.default.Fragment, null, (0, _react2.jsx)(_inputFooter.InputFooter, {
    ref: ref,
    id: isLinkedToInput ? id : undefined,
    error: error,
    warning: warning,
    icon: icon,
    borderRadius: borderRadius,
    iconAltText: iconAltText
  }, (0, _react2.jsx)(_layout.Row, {
    flex: 1,
    justifyContent: "space-between",
    flexWrap: "wrap",
    gridGap: 'S100'
  }, (0, _react2.jsx)(_inputFooter.InputFooterText, {
    "aria-hidden": "true",
    variant: _text.TextVariant.BodySM,
    color: _constants.FOOTER_TEXT_FONT_COLOR
  }, children), isNotOnMobile && (0, _react2.jsx)(_inputFooter.InputFooterText, {
    variant: _text.TextVariant.BodySM,
    fontSize: _constants.FOOTER_TEXT_FONT_SIZE,
    color: _constants.FOOTER_TEXT_FONT_COLOR
  }, __additionalInfo))), (0, _react2.jsx)(_screenReaderOnly.ScreenReaderOnly, {
    "aria-atomic": ariaAtomic,
    "aria-live": ariaLive,
    "aria-relevant": ariaRelevant,
    role: ariaRole,
    "data-test-component": "StencilScreenReaderOnly"
  }, announcementText && (!!warning || !!error) ? appendedAnnouncementText : announcementText));
};
RecommendedLengthFooterBase.displayName = 'RecommendedLengthFooter';
const RecommendedLengthFooter = (0, _stencilReactDesc.describe)(RecommendedLengthFooterBase).description('Footer displaying the recommended length that should be entered in an input element.');
RecommendedLengthFooter.propTypes = {
  ...(0, _utils2.omit)(_inputFooter.InputFooter.describedPropTypes, ['icon']),
  progress: _stencilReactDesc.PropTypes.number.isRequired.description('Ratio of entered text and the recommended length to be displayed by the progress icon.'),
  role: _stencilReactDesc.PropTypes.string.description('Sets `role` attribute on the textarea.'),
  'aria-live': _stencilReactDesc.PropTypes.oneOf(['polite', 'assertive', 'off']).description("Sets 'aria-live' attribute on the textarea."),
  'aria-atomic': _stencilReactDesc.PropTypes.string.description("Sets 'aria-atomic' attribute on the textarea."),
  'aria-relevant': _stencilReactDesc.PropTypes.string.description("Sets 'aria-relevant' attribute on the textarea.")
};
var _default = exports.default = RecommendedLengthFooter;